import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAlerts } from '../../context/AlertContext';
import { useAuth } from '../../context/AuthContext';
import AlertsListModal from '../alerts/AlertsListModal';
import logo from '../../assets/logo.png';
import '../../styles/layout.css';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useAlerts();
  const { user, logout, hasMenuAccess } = useAuth();

  const handleLogout = async () => {
    setUserMenuOpen(false);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Navigate to login anyway
      navigate('/login');
    }
  };

  const navItems = [
    { name: 'Monitoring', path: '/monitoring', icon: 'monitor', show: hasMenuAccess('monitoring') },
    { name: 'Charts', path: '/charts', icon: 'chart', show: hasMenuAccess('chart') },
    { name: 'Speeding', path: '/speeding', icon: 'speeding', show: hasMenuAccess('speeding') },
    { name: 'Settings', path: '/settings', icon: 'settings', show: hasMenuAccess('settings') },
    { name: 'Users', path: '/users', icon: 'users', show: hasMenuAccess('users') },
  ];
  
  const isActive = (path) => {
    if (path === '/monitoring') {
      return location.pathname === '/' || location.pathname === '/monitoring';
    }
    return location.pathname.startsWith(path);
  };
  
  const renderIcon = (iconName) => {
    const icons = {
      monitor: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      chart: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      bell: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      speeding: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      settings: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      users: (
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m5-4a4 4 0 100-8 4 4 0 000 8zm6 4a4 4 0 100-8" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };
  
  return (
    <nav className="navbar">
      <div className="navbar__container">
        <div className="navbar__content">
          {/* Logo and brand */}
          <div className="navbar__brand">
            <Link to="/" className="navbar__brand-link">
              <div className="Logo">
                <img src={logo} alt="Virmatics" className="navbar__logo" />
              </div>
              <span className="navbar__title">
                Virmatics -Temperature Monitoring System
              </span>
            </Link>
          </div>
          
          {/* Desktop navigation */}
          <div className="navbar__nav">
            {navItems
              .filter(item => item.show)
              .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`navbar__link ${isActive(item.path) ? 'navbar__link--active' : ''}`}
                >
                  {renderIcon(item.icon)}
                  <span>{item.name}</span>
                </Link>
            ))}
            {/* Alerts button - opens modal */}
            <button
              onClick={() => setAlertsModalOpen(true)}
              className="navbar__link"
            >
              {renderIcon('bell')}
              <span>Alerts</span>
              {unreadCount > 0 && (
                <span className="navbar__badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
          
          {/* Right side actions */}
          <div className="navbar__actions">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="navbar__theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>
            
            {/* User menu */}
            <div className="navbar__user-menu">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="navbar__user-button"
                aria-label="User menu"
              >
                <div className="navbar__user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              </button>
              
              {userMenuOpen && (
                <div className="navbar__user-dropdown">
                  {/* User Info */}
                  <div className="navbar__user-info">
                    <p className="navbar__user-name">
                      {user?.name || 'User'}
                    </p>
                    <p className="navbar__user-email">
                      {user?.email || ''}
                    </p>
                    {user?.role && (
                      <span className="navbar__user-role">
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    )}
                  </div>
                  
                  {/* Menu Items */}
                  {hasMenuAccess('history') && (
                    <Link
                      to="/history"
                      onClick={() => setUserMenuOpen(false)}
                      className="navbar__menu-item"
                    >
                      <svg fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      History
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="navbar__logout-button"
                  >
                    <svg fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="navbar__mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              <svg fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="navbar__mobile-menu">
          <div className="navbar__mobile-menu-content">
            {navItems.filter(item => item.show).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`navbar__mobile-link ${isActive(item.path) ? 'navbar__mobile-link--active' : ''}`}
              >
                {renderIcon(item.icon)}
                <span>{item.name}</span>
              </Link>
            ))}
            {/* Alerts button - opens modal */}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setAlertsModalOpen(true);
              }}
              className="navbar__mobile-link"
            >
              {renderIcon('bell')}
              <span>Alerts</span>
              {unreadCount > 0 && (
                <span className="navbar__mobile-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Alerts Modal */}
      <AlertsListModal
        isOpen={alertsModalOpen}
        onClose={() => setAlertsModalOpen(false)}
      />
    </nav>
  );
};

export default Navbar;

